import { useEffect, useMemo, useState } from 'react';
import { Loader2, Store } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/lib/toast';
import { insertChatMessage } from '@/queries/chat';
import { createMeetupRequest } from '@/queries/offers';
import { useProfileLocation } from '@/hooks/useProfileLocation';
import { useGameStores } from '@/hooks/useGameStores';
import { calculateDistanceMiles } from '@/lib/distanceUtils';
import { cn } from '@/lib/utils';
import { GlassModal } from '@/components/ui/glass-modal';

interface ProposeMeetupSheetProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	conversationId: string;
	currentUserId: string | undefined;
	acceptedInterestId: string | null;
	itemTitle?: string | null;
}

export const ProposeMeetupSheet = ({
	open,
	onOpenChange,
	conversationId,
	currentUserId,
	acceptedInterestId,
	itemTitle,
}: ProposeMeetupSheetProps) => {
	const [location, setLocation] = useState('');
	const [datetime, setDatetime] = useState('');
	const [notes, setNotes] = useState('');
	const [busy, setBusy] = useState(false);
	const { coords } = useProfileLocation();
	const { data: stores = [] } = useGameStores(open && !!coords);

	const lgsSuggestions = useMemo(() => {
		if (!coords) return [] as Array<{ id: string; name: string; city: string | null; distance: number }>;
		const withDist = stores
			.map((s: any) => ({
				id: s.id as string,
				name: s.name as string,
				city: (s.city as string | null) ?? null,
				distance: calculateDistanceMiles(coords.lat, coords.lng, Number(s.lat), Number(s.lng)),
			}))
			.filter((s) => Number.isFinite(s.distance) && s.distance <= 25);
		withDist.sort((a, b) => a.distance - b.distance);
		return withDist.slice(0, 3);
	}, [stores, coords]);

	useEffect(() => {
		if (!open) return;
		setLocation('');
		setNotes('');
		const d = new Date();
		d.setDate(d.getDate() + 1);
		d.setMinutes(0, 0, 0);
		const iso = new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
		setDatetime(iso);
	}, [open]);

	const submit = async () => {
		if (!currentUserId || !acceptedInterestId) {
			toast.error('Accept an offer first to schedule a pickup');
			return;
		}
		if (!location.trim() || !datetime) {
			toast.error('Add a location and time');
			return;
		}
		setBusy(true);
		try {
			const proposedAtIso = new Date(datetime).toISOString();
			const { data: meetup, error } = await createMeetupRequest({
				interestId: acceptedInterestId,
				proposedBy: currentUserId,
				proposedLocation: location.trim(),
				proposedAtIso,
				notes: notes.trim() || null,
			});
			if (error) throw error;

			await insertChatMessage({
				conversationId,
				senderId: currentUserId,
				kind: 'meetup',
				body: `Pickup proposed at ${location.trim()}`,
				metadata: {
					meetup_request_id: meetup.id,
					interest_id: acceptedInterestId,
					item_title: itemTitle ?? null,
				},
			});
			toast.success('Pickup proposed');
			onOpenChange(false);
		} catch (err: any) {
			toast.error('Could not propose pickup', { description: err?.message });
		} finally {
			setBusy(false);
		}
	};

	return (
		<GlassModal
			open={open}
			onOpenChange={onOpenChange}
			title="Propose local pickup"
			description="Meet at a safe, public location."
		>
			<div className="space-y-1.5">
				<h2 className="text-lg font-semibold">Propose local pickup</h2>
				<p className="text-sm text-muted-foreground">Meet at a safe, public location.</p>
			</div>

			<div className="space-y-4 py-4">
				<div>
					<label className="text-[12px] font-medium text-muted-foreground mb-1.5 block">
						Where
					</label>
					{lgsSuggestions.length > 0 && (
						<div className="mb-2 -mx-0.5 flex flex-wrap gap-1.5">
							{lgsSuggestions.map((s) => {
								const label = s.city ? `${s.name} · ${s.city}` : s.name;
								const selected = location.trim() === label;
								return (
									<button
										key={s.id}
										type="button"
										onClick={() => setLocation(label)}
										className={cn(
											'inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full text-[11px] border transition-colors',
											selected
												? 'bg-primary/15 border-primary/40 text-primary'
												: 'bg-white/[0.04] border-white/[0.08] text-muted-foreground hover:text-foreground hover:bg-white/[0.08]'
										)}
									>
										<Store className="h-3 w-3 opacity-70" />
										<span className="truncate max-w-[160px]">{s.name}</span>
										<span className="opacity-60">· {s.distance.toFixed(1)}mi</span>
									</button>
								);
							})}
						</div>
					)}
					<Input
						value={location}
						onChange={(e) => setLocation(e.target.value)}
						placeholder="e.g. Starbucks at 5th & Main"
						style={{ fontSize: 16 }}

					/>
				</div>
				<div>
					<label className="text-[12px] font-medium text-muted-foreground mb-1.5 block">
						When
					</label>
					<Input
						type="datetime-local"
						value={datetime}
						onChange={(e) => setDatetime(e.target.value)}
						style={{ fontSize: 16 }}
					/>
				</div>
				<div>
					<label className="text-[12px] font-medium text-muted-foreground mb-1.5 block">
						Notes (optional)
					</label>
					<Textarea
						value={notes}
						onChange={(e) => setNotes(e.target.value)}
						rows={2}
						placeholder="e.g. I'll be wearing a red hoodie"
						style={{ fontSize: 16 }}
					/>
				</div>
			</div>
			<div className="flex gap-2 pt-2">
				<Button variant="outline" className="flex-1" onClick={() => onOpenChange(false)}>
					Cancel
				</Button>
				<Button className="flex-1" onClick={submit} disabled={busy}>
					{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Send'}
				</Button>
			</div>
		</GlassModal>
	);
};

export default ProposeMeetupSheet;
